import { buildTranslate } from 'locales'
import findLastIndex from 'lodash/findLastIndex'
import { useMemo } from 'react'

import ThanxAIMessage from 'components/AI/ThanxAIMessage'
import { useAIChat } from 'contexts/AIChatConnectionProvider'
import {
  analyzeSegmentStates,
  hasAnySavedSegmentInMessages,
} from 'utils/segmentStateAnalysis'
import './ThanxAIMessageList.scss'

const t = buildTranslate('components.ai.conversation')

// Re-export for backward compatibility with parent components
export { hasAnySavedSegmentInMessages }

export interface ThanxAIMessageListProps {
  bottomSpacing?: number
  onMessageSubmit?: (message: string) => void
}

const ThanxAIMessageList = ({
  bottomSpacing = 144,
  onMessageSubmit,
}: ThanxAIMessageListProps) => {
  // Get WebSocket data from provider
  const { messages, currentStreamingMessage, isLoading, awaitingResponse } =
    useAIChat()
  // Determine if a message should show the sender label (ThanxAI/User)
  // Show label only if it's the first message or previous message is from different sender
  const shouldShowLabel = (index: number): boolean => {
    if (index === 0) return true
    return messages[index].type !== messages[index - 1].type
  }

  // Memoize segment state analysis to avoid recalculating on every render
  const {
    hasAnySavedSegment,
    latestDraftSegmentMessageId,
    latestSavedSegmentMessageId,
  } = useMemo(() => analyzeSegmentStates(messages), [messages])

  // Callout actions belong to the turn after the merchant's last message or answered picker, and wait for the AI like the composer
  const latestTurnStart =
    findLastIndex(
      messages,
      ({ type, pickerSubmitted }) => type === 'user' || Boolean(pickerSubmitted)
    ) + 1
  const onCalloutAction = hasAnySavedSegment ? undefined : onMessageSubmit
  const calloutActionsDisabled =
    awaitingResponse || Boolean(currentStreamingMessage)

  return (
    <div
      className="thanx-ai-conversation__chat-area"
      role="log"
      aria-label={t('chat_history_label')}
      aria-live="polite"
      tabIndex={0}
      style={{ paddingBottom: `${bottomSpacing}px` }}
    >
      {messages.map((message, index) => (
        <ThanxAIMessage
          key={message.id}
          message={message}
          showLabel={shouldShowLabel(index)}
          isLatestDraftSegment={message.id === latestDraftSegmentMessageId}
          isLatestSavedSegment={message.id === latestSavedSegmentMessageId}
          hasAnySavedSegment={hasAnySavedSegment}
          onCalloutAction={
            index >= latestTurnStart ? onCalloutAction : undefined
          }
          calloutActionsDisabled={calloutActionsDisabled}
        />
      ))}
      {currentStreamingMessage && (
        <ThanxAIMessage
          key={`${currentStreamingMessage.id}-streaming`}
          message={currentStreamingMessage}
          showLabel={
            messages.length === 0 ||
            messages[messages.length - 1].type !== currentStreamingMessage.type
          }
          onCalloutAction={onCalloutAction}
          calloutActionsDisabled={calloutActionsDisabled}
        />
      )}
      {isLoading && !currentStreamingMessage && messages.length > 0 && (
        <ThanxAIMessage
          key="loading"
          message={{
            id: 'loading',
            content: '',
            type: 'ai',
            timestamp: new Date(),
            isMarkdown: false,
            sequenceNumber: 0,
            clientId: 'loading-message',
            isOptimistic: false,
          }}
          showLabel={
            messages.length === 0 || messages[messages.length - 1].type !== 'ai'
          }
          isLoading={true}
        />
      )}
    </div>
  )
}

export default ThanxAIMessageList
